import { KlydoReviewStatus, PopulatedKlydo } from '../../../../api/klydo';
import {
  CollectionReviewStates,
  MarketplaceStatus,
} from '../../../../api/collections';
import { DRAFT, PENDING, APPROVED, REJECTED } from '../../../../consts/colors';

/** Which icon the row carries; the component owns the SVGs. */
export type StatusRowKind = 'public' | 'collection';

export type StatusRow = { kind: StatusRowKind; color: string; text: string };

export type StatusKlydo = Pick<
  PopulatedKlydo,
  'public' | 'collection' | 'unlisted'
>;

/** Matches the "Updated At" format used on the card: DD.MM.YY. */
const formatGoLiveDate = (timestamp: number): string =>
  new Date(timestamp)
    .toLocaleDateString(undefined, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '.');

const notPublicRow: StatusRow = {
  kind: 'public',
  color: DRAFT,
  text: 'Not public',
};

/**
 * The globe (public) row.
 *
 * `public.status` is consulted FIRST, and on its own. It is the serving gate, so a
 * klydo that is served is "Public" whatever its review record says — and the two
 * disagree constantly on migrated data: 527 production klydos are served while
 * their record still reads `pending`, and 2168 are served with no `state` at all.
 * Keying this on the state alone showed "Pending review" on live klydos, and no row
 * at all for the 2168.
 */
const bannedRow: StatusRow = {
  kind: 'public',
  color: REJECTED,
  text: 'Banned',
};

const getPublicRow = (
  state?: KlydoReviewStatus,
  isPublic?: boolean,
  requestedTimestamp?: number | null,
  longPendingLabel?: boolean,
  isWithdrawnAfterVerdict?: boolean,
): StatusRow => {
  if (isPublic) {
    return { kind: 'public', color: APPROVED, text: 'Public' };
  }

  // The state is gone, the rejection it left behind is not, and that is the thing
  // the artist still has to answer — same row the Rejected tab it now sits in.
  if (isWithdrawnAfterVerdict) {
    return { kind: 'public', color: REJECTED, text: 'Rejected' };
  }

  switch (state) {
    case KlydoReviewStatus.Pending:
      return {
        kind: 'public',
        color: PENDING,
        text: longPendingLabel ? 'Pending review' : 'Review',
      };
    case KlydoReviewStatus.Approved:
      // Approved but not served yet, so say which date it is waiting on. The date
      // can read as just-passed while the cron catches up. Without a date at all it
      // is simply not public, and claiming otherwise would be false.
      return requestedTimestamp
        ? {
            kind: 'public',
            color: APPROVED,
            text: `Public (from ${formatGoLiveDate(requestedTimestamp)})`,
          }
        : notPublicRow;
    case KlydoReviewStatus.Rejected:
      return { kind: 'public', color: REJECTED, text: 'Rejected' };
    default:
      return notPublicRow;
  }
};

/**
 * The box (collection) row, driven by the parent collection's OWN review state —
 * the klydo carries no collection-review field of its own.
 *
 * Null for a DRAFT collection. `CollectionReviewStates.Draft` is a real member and
 * used to fall through to "Collection (Review)", so a klydo sitting in a collection
 * its artist had not submitted yet claimed to be under review. Nothing is under
 * review, so the design rule applies: no state, no row.
 */
const getCollectionRow = (
  state: CollectionReviewStates,
  isInMarketplace: boolean,
): StatusRow | null => {
  if (state === CollectionReviewStates.Draft) {
    return null;
  }
  // Rejection is the one state the artist must always see.
  if (state === CollectionReviewStates.Rejected) {
    return {
      kind: 'collection',
      color: REJECTED,
      text: 'Collection (Rejected)',
    };
  }
  // Marketplace is claimed only once the collection is really on sale
  // (`marketplace.status === true`). Approval alone does not count: until it goes
  // live the artist keeps seeing "Review", so there is no intermediate status.
  if (isInMarketplace && state === CollectionReviewStates.Approved) {
    return {
      kind: 'collection',
      color: APPROVED,
      text: 'Collection (Marketplace)',
    };
  }
  return { kind: 'collection', color: PENDING, text: 'Collection (Review)' };
};

/**
 * The rows a klydo's status shows: zero, one or two.
 *
 * Pure, and exported separately from the component so the mapping can be tested
 * directly — it is the densest decision in the publication work and three separate
 * bugs survived in it while it had no tests.
 */
export const getKlydoStatusRows = (
  klydo: StatusKlydo | undefined,
  longPendingLabel?: boolean,
): StatusRow[] => {
  const publicState = klydo?.public?.reviewStatus?.state;
  const isPublic = !!klydo?.public?.status;
  // Withdrawing after a rejection clears the state and keeps the note, and only a
  // reviewer writes that note. Without this the klydo shows no row at all while
  // the Rejected tab lists it, so the card reads as a draft nobody has judged.
  const isWithdrawnAfterVerdict =
    !publicState && !!klydo?.public?.reviewStatus?.message;
  const collectionState = klydo?.collection?.reviewStatus?.state;
  const isInMarketplace =
    klydo?.collection?.marketplace?.status === MarketplaceStatus.True;

  const rows: StatusRow[] = [];

  // A ban wins over the review status in every case, so the artist sees one
  // answer. The review record underneath is untouched and comes back when the ban
  // is lifted. A banned klydo cannot be public (the API refuses to ban one).
  if (klydo?.unlisted) {
    rows.push(bannedRow);
  } else if (isPublic || publicState || isWithdrawnAfterVerdict) {
    // Served, OR a review record with a verdict. `isPublic` has to be part of this
    // gate: 2168 production klydos are served with no `state`, and gating on the
    // state alone left every one of them with no row while the gallery still
    // listed them under Public.
    rows.push(
      getPublicRow(
        publicState,
        isPublic,
        klydo?.public?.reviewStatus?.requestPayload?.timestamp,
        longPendingLabel,
        isWithdrawnAfterVerdict,
      ),
    );
  }

  if (collectionState) {
    const collectionRow = getCollectionRow(collectionState, isInMarketplace);
    if (collectionRow) {
      rows.push(collectionRow);
    }
  }

  // Nothing started → no row at all, matching the gallery design, which only shows a
  // row that has something to say.
  return rows;
};
